import { useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ArrowBack } from '@material-ui/icons';
import {
  Paper,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Dialog,
  Grid,
  Select,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  Button
} from '@material-ui/core';
import '@xyflow/react/dist/style.css';
import { Background, Controls, MiniMap, ReactFlow, useEdgesState, useNodesState } from '@xyflow/react';
import { deleteCalcGraph, getCalcGraph, getCalcTemplates, getPricingVisualizer, getProductV2Data, getRoles, getVisualizer, saveCalcGraph, updateCalcGraph } from '../../../Actions';
import CustomNode from './CustomNode';
import CustomEdge from './CustomEdge';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { CalculationVisualizerContext } from '../../../Context/CalculationVisualizerContextProvider';
import NewNodeModal from './NewNodeModal';
import EditNodeModal from './EditNodeModal';

// The editor works on one graph at a time: a read-only stock template
// ({ type: 'template', key }) or an editable saved graph ({ type: 'saved',
// id }). Templates can only be copied ("Save as"); saved graphs persist
// per edit, and when the edited graph is the live pricing model the server
// rematerializes the live config.
const sourceToVisualizerParam = (source) =>
  source?.type === 'template' ? { template: source.key } : source?.type === 'saved' ? { graphId: source.id } : undefined;

export default function FlowView() {
  const history = useHistory();
  const { graph, setGraph, graphSource, setGraphSource, refreshGraph, setRefreshGraph, isPromoFlow, setIsPromoFlow, includesPricing, setIsIncludesPricing, selectedOrientation, setSelectedOrientation, isNewNodeModalOpen, isEditNodeModalOpen, setSelectedNode } = useContext(CalculationVisualizerContext);
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [productUUID, setProductUUID] = useState("");
  const [productData, setProductData] = useState();
  const [selectedOption, setSelectedOption] = useState("");
  const [isPromoFlowAvailable, setIsPromoFlowAvailable] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [savedGraphs, setSavedGraphs] = useState([]);
  // Which model is live on the site ('margin' | 'markup' | 'graph:<id>').
  const [liveSelection, setLiveSelection] = useState(null);

  const sourceValue = graphSource?.type === 'template' ? graphSource.key : graphSource?.type === 'saved' ? `graph:${graphSource.id}` : '';
  const sourceIsLive = !!sourceValue && sourceValue === liveSelection;

  // Load the model catalog and open on whatever is live (fall back to the
  // first stock template so the editor never starts empty).
  const refreshModels = async (preferredValue) => {
    return getCalcTemplates().then((r) => {
      const tpl = r.data.templates || [];
      const graphs = r.data.graphs || [];
      setTemplates(tpl);
      setSavedGraphs(graphs);
      setLiveSelection(r.data.selection);

      const target = preferredValue ?? r.data.selection ?? tpl[0]?.key;
      loadSourceByValue(target, tpl, graphs);
    }).catch(() => {});
  };

  const loadSourceByValue = (value, tpl = templates, graphs = savedGraphs) => {
    if (!value) return;

    if (/^graph:\d+$/.test(value)) {
      const id = Number(value.split(':')[1]);
      const saved = graphs.find((g) => g.calc_graph_id === id);
      if (!saved) return;
      setGraphSource({ type: 'saved', id, name: saved.name, readOnly: false });
    } else {
      const template = tpl.find((t) => t.key === value);
      if (!template) return;
      setGraphSource({ type: 'template', key: template.key, name: template.name, readOnly: true });
    }

    setRefreshGraph(true);
  };

  useEffect(() => {
    refreshModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleModelSelect = (value) => {
    if (value === '__new') {
      handleNewGraph();
      return;
    }
    loadSourceByValue(value);
  };

  const handleNewGraph = async () => {
    const name = window.prompt('Name for the new graph:');
    if (!name || !name.trim()) return;

    await saveCalcGraph(name.trim(), { start: { label: 'Start', next: 'end' }, end: { label: 'End' } }, 'custom')
      .then((r) => refreshModels(`graph:${r.data.calc_graph_id}`))
      .catch(() => window.alert('Could not create the graph.'));
  };

  const handleSaveAsCopy = async () => {
    if (!graph) return;
    const name = window.prompt(`Save a copy of "${graphSource?.name}" as:`);
    if (!name || !name.trim()) return;

    await saveCalcGraph(name.trim(), graph, graphSource?.type === 'template' ? graphSource.key : 'custom')
      .then((r) => refreshModels(`graph:${r.data.calc_graph_id}`))
      .catch(() => window.alert('Could not save the copy.'));
  };

  const handleRename = async () => {
    if (graphSource?.type !== 'saved') return;
    const name = window.prompt('Rename graph:', graphSource.name);
    if (!name || !name.trim() || name.trim() === graphSource.name) return;

    await updateCalcGraph(graphSource.id, undefined, name.trim())
      .then(() => refreshModels(`graph:${graphSource.id}`))
      .catch(() => window.alert('Could not rename the graph.'));
  };

  const handleDelete = async () => {
    if (graphSource?.type !== 'saved') return;
    if (!window.confirm(`Delete "${graphSource.name}"?`)) return;

    await deleteCalcGraph(graphSource.id)
      .then(() => refreshModels(liveSelection))
      .catch((err) => window.alert(err?.response?.data?.message || 'Could not delete the graph.'));
  };

  // ---- JSON view: edit/paste the raw graph instead of using the canvas ----
  const [viewMode, setViewMode] = useState('graph');
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState(null);

  // Catches the mistakes that would otherwise 500 at render/price time:
  // bad JSON, missing start/end, references to nodes that don't exist.
  const validateGraphObject = (obj) => {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return 'The graph must be a JSON object of nodes.';
    if (!obj.start) return 'The graph needs a "start" node.';
    if (!obj.end) return 'The graph needs an "end" node.';

    const refs = [];
    Object.entries(obj).forEach(([id, node]) => {
      if (node?.next) refs.push([id, node.next]);
      node?.condition?.operands?.forEach((o) => { if (o?.next) refs.push([id, o.next]); });
      if (node?.condition?.default?.next) refs.push([id, node.condition.default.next]);
    });

    const missing = refs.filter(([, target]) => !obj[target]);
    if (missing.length) {
      return `Missing node(s): ${[...new Set(missing.map(([from, target]) => `"${target}" (referenced by "${from}")`))].join(', ')}`;
    }

    return null;
  };

  // Keep the textarea in step when the loaded graph changes while in JSON
  // view (switching graphs, or the post-save refresh). Canvas edits can't
  // happen while the JSON view is open, so this never clobbers typing.
  useEffect(() => {
    if (viewMode === 'json') {
      setJsonText(graph ? JSON.stringify(graph, null, 2) : '');
      setJsonError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graph]);

  const handleViewModeChange = (e, newMode) => {
    if (!newMode || newMode === viewMode) return;

    if (newMode === 'json') {
      setJsonText(graph ? JSON.stringify(graph, null, 2) : '');
      setJsonError(null);
    }

    setViewMode(newMode);
  };

  const handleSaveJson = async () => {
    if (graphSource?.type !== 'saved') return;

    let parsed;
    try {
      parsed = JSON.parse(jsonText);
    } catch (err) {
      setJsonError(`Invalid JSON: ${err.message}`);
      return;
    }

    const validationError = validateGraphObject(parsed);
    if (validationError) {
      setJsonError(validationError);
      return;
    }

    await updateCalcGraph(graphSource.id, parsed)
      .then(() => {
        setJsonError(null);
        setJsonText(JSON.stringify(parsed, null, 2));
        setGraph(parsed);
        setRefreshGraph(true);
      })
      .catch((err) => setJsonError(err?.response?.data?.message || 'Could not save the graph.'));
  };

  const [nodes, setNodes, onNodesChange] = useNodesState([]); 
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  const nodeTypes = useMemo(() => ({ default: CustomNode }), []);
  const edgeTypes = useMemo(() => ({ default: CustomEdge }), []);

  useEffect(() => {
    async function getPageData() {
      await getRoles()
        .then((result) => {
          setRoles(result.data.roleList ?? []);
        })
        .catch((err) => {
          setRoles([]);
        });
    }

    getPageData();
  }, [])

  // Keep context.graph (what the node modals edit) in sync with the loaded
  // source: templates come from the catalog payload, saved graphs from the
  // server so edits are always built on the latest stored version.
  useEffect(() => {
    async function refreshGraphData() {
      if (graphSource?.type === 'template') {
        const template = templates.find((t) => t.key === graphSource.key);
        setGraph(template?.graph ? { ...template.graph } : null);
        return;
      }

      if (graphSource?.type === 'saved') {
        await getCalcGraph(graphSource.id)
          .then((result) => setGraph(result.data.graph))
          .catch(() => setGraph(null));
        return;
      }

      setGraph(null);
    }

    if (!refreshGraph) return;

    refreshGraphData()
    setRefreshGraph(false);
  }, [refreshGraph, graphSource])

  async function getDataWithoutCalculations(selectedOrientation) {
    await getVisualizer(selectedOrientation, sourceToVisualizerParam(graphSource))
      .then((result) => {
        const { nodes: newNodes, edges: newEdges } = result.data;
        
        setNodes([ ...newNodes ]);
        setEdges([ ...newEdges ]);
      })
      .catch((err) => {
        setNodes([]);
        setEdges([]);
      });
  }

  async function getDataWithCalculations(productUUID, selectedRole, selectedOption, selectedOrientation) {
    await getPricingVisualizer(productUUID, selectedRole, selectedOption, selectedOrientation, sourceToVisualizerParam(graphSource))
      .then((result) => {
        const { nodes: newNodes, edges: newEdges } = result.data;

        setNodes([ ...newNodes ]);
        setEdges([ ...newEdges ]);
      })
      .catch((err) => {
        setNodes([]);
        setEdges([]);
      });
  }

  useEffect(() => {
    setNodes([]);
    setEdges([]);

    // Nothing to render until a graph source is loaded.
    if (!graphSource) return;

    if (!selectedOption || !productUUID || !selectedRole) {
      getDataWithoutCalculations(selectedOrientation);
      setIsIncludesPricing(false);
    } else {
      getDataWithCalculations(productUUID, selectedRole, selectedOption, selectedOrientation);
      setIsIncludesPricing(true);
    };

    return () => {
      setSelectedNode(null);
    }
  }, [selectedOption, selectedOrientation, selectedRole, refreshGraph, graphSource])

  useEffect(() => {
    if (includesPricing && nodes?.[0]?.data?.nodeData?.pricingData?.promotion) {
      setIsPromoFlowAvailable(true);
      return;
    }

    setIsPromoFlowAvailable(false);
    setIsPromoFlow(false);
  }, [nodes])

  const getProduct = async (productUUID, roleId) => {
    if (!productUUID || !roleId) return;

    await getProductV2Data(`product/v2/${productUUID}/${roleId}?calc=V2`)
      .then(result => {
        setProductData({ ...result.data.product });
      })
      .catch(err => {
        setProductData(undefined);
      })
  }

  const nodeColor = (node) => {
    if (['start', 'end'].includes(node.id)) {
      return node.id === 'start' ? '#388E3C' : '#f44336';
    }
    
    return '#999999';
  }

  return (
    <Paper style={{ height: '80vh', padding: '16px' }}>
      <Paper style={{ width: "350px", position: 'absolute', padding: '10px', zIndex: 1000 }}>
        <Grid container spacing={2} style={{ width: "100%", zIndex: 50, position: "relative" }}>
          <Grid item xs={12}>
            <Button
              size="small"
              startIcon={<ArrowBack />}
              onClick={() => history.push('/internal-tools')}
            >
              Internal Tools
            </Button>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth variant="filled">
              <InputLabel shrink id="graph-list-label">Graph</InputLabel>
              <Select
                value={sourceValue}
                displayEmpty
                id="graph-list-label"
                onChange={(e) => handleModelSelect(e.target.value)}
              >
                <MenuItem value="" disabled>Select a graph</MenuItem>
                {templates.map((t) => (
                  <MenuItem key={t.key} value={t.key}>
                    {t.name}{liveSelection === t.key ? ' (live)' : ''} — standard
                  </MenuItem>
                ))}
                {savedGraphs.map((g) => (
                  <MenuItem key={g.calc_graph_id} value={`graph:${g.calc_graph_id}`}>
                    {g.name}{liveSelection === `graph:${g.calc_graph_id}` ? ' (live)' : ''}
                  </MenuItem>
                ))}
                <MenuItem value="__new">+ New graph…</MenuItem>
              </Select>
            </FormControl>
            {graphSource?.readOnly ? (
              <Typography variant="caption" component="div" style={{ marginTop: 4, color: 'rgba(0,0,0,0.6)' }}>
                Standard models are read-only — save a copy to make changes.
              </Typography>
            ) : undefined}
            {sourceIsLive && graphSource?.type === 'saved' ? (
              <Typography variant="caption" component="div" style={{ marginTop: 4, color: '#b71c1c' }}>
                This graph is the live pricing model — edits change prices immediately.
              </Typography>
            ) : undefined}
            <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
              <Button size="small" variant="outlined" disabled={!graph} onClick={handleSaveAsCopy}>
                Save as…
              </Button>
              {graphSource?.type === 'saved' ? (
                <Button size="small" variant="outlined" onClick={handleRename}>
                  Rename…
                </Button>
              ) : undefined}
              {graphSource?.type === 'saved' ? (
                <Button size="small" disabled={sourceIsLive} onClick={handleDelete} style={{ color: sourceIsLive ? undefined : '#b71c1c' }}>
                  Delete
                </Button>
              ) : undefined}
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                size="small"
                onChange={handleViewModeChange}
                aria-label="editor view mode"
                style={{ marginLeft: 'auto' }}
              >
                <ToggleButton value="graph" aria-label="graph view" style={viewMode === 'graph' ? { backgroundColor: '#142E3E', color: '#28C4FC' } : { backgroundColor: '#00000017', color: 'initial' }}>
                  Graph
                </ToggleButton>
                <ToggleButton value="json" aria-label="json view" style={viewMode === 'json' ? { backgroundColor: '#142E3E', color: '#28C4FC' } : { backgroundColor: '#00000017', color: 'initial' }}>
                  JSON
                </ToggleButton>
              </ToggleButtonGroup>
            </div>
          </Grid>
          {/* <Grid item xs={12}>
            <ToggleButtonGroup
              value={selectedOrientation}
              exclusive
              size='small'
              onChange={(e, newValue) => {
                if (!newValue) return;

                setSelectedOrientation(newValue)
              }}
              aria-label="text alignment"
            >
              <ToggleButton value='h' aria-label="horizontal" style={selectedOrientation === 'h' ? {backgroundColor: "#142E3E", color: "#28C4FC"} : {backgroundColor: "#00000017", color: "initial"}}>
                Horizontal
              </ToggleButton>
              <ToggleButton value='v' aria-label="vertical" style={selectedOrientation === 'v' ? {backgroundColor: "#142E3E", color: "#28C4FC"} : {backgroundColor: "#00000017", color: "initial"}}>
                Vertical
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid> */}
          <Grid item xs={12}>
            <FormControl fullWidth variant="filled">
              <InputLabel shrink id="role-list-label">Selected Role</InputLabel>
              <Select
                value={selectedRole ?? ""}
                displayEmpty
                id="role-list-label"
                onChange={(e) => setSelectedRole(e.target.value)}
              >
                <MenuItem value="" disabled>Please select one of the following</MenuItem>
                {
                  roles?.map(role => {
                    return <MenuItem key={role.role_id} value={role.role_id}>{role.role_name}</MenuItem>
                  })
                }
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              variant="filled"
              label="Product UUID"
              placeholder="Enter product UUID"
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
              value={productUUID}
              onChange={(e) => setProductUUID(e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <Button 
              variant="contained" 
              style={{color: "#28C4FC", backgroundColor: "#142E3E"}}
              disabled={!selectedRole || !productUUID}
              onClick={() => getProduct(productUUID, selectedRole)}
            >
              Get Product
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button 
              variant="contained" 
              disabled={!productData}
              onClick={() => {
                setProductUUID("");
                setProductData(undefined);
                setSelectedOption("");
              }}
            >
              Clear Product
            </Button>
          </Grid>
          { productData?.options?.length ? (
              <Grid item xs={12}>
                <FormControl fullWidth variant="filled">
                  <InputLabel shrink id="option-list-label">Selected Option</InputLabel>
                  <Select
                    value={selectedOption ?? ""}
                    displayEmpty
                    id="option-list-label"
                    onChange={(e) => setSelectedOption(e.target.value)}
                  >
                    <MenuItem value="" disabled>Please select one of the following</MenuItem>
                    { 
                      productData.options.map(option => {

                        let optionDetails = "";

                        if (option.pricing.retail_price) {
                          optionDetails = "$" + option.pricing.retail_price.toFixed(2);
                        }

                        if (option.is_promo_active) {
                          optionDetails = "$" + option.promotion_pricing.retail_price.toFixed(2) + " (Promo)";
                        }

                        if (option.is_discontinued) {
                          optionDetails = "Discontinued";
                        } else if (option.is_call_for_price) {
                          optionDetails = "Call For Price";
                        }

                        if (option.uom) {
                          optionDetails += optionDetails !== "" ? ` - ${option.uom}` : option.uom;
                        }

                        if (option.basis_cd) {
                          optionDetails += optionDetails !== "" ? ` - ${option.basis_cd}` : option.basis_cd;
                        }

                        return (
                          <MenuItem key={option.cost_id} value={option.cost_id}>
                            <Typography variant="body2" color={option.is_discontinued ? "error" : "inherit"}>
                              <b>{option.color_name}</b>
                              <br></br>
                              { option.distributor ? (
                                  <>
                                    {option.distributor}
                                    <br></br>
                                  </>
                                ) : undefined
                              }
                              { option.fob_origin ? (
                                  <>
                                    {option.fob_origin}
                                    <br></br>
                                  </>
                                ) : undefined
                              }
                              <span style={{color: option.is_promo_active ? "#f44336" : "inherit"}}>{optionDetails}</span>  
                            </Typography>
                          </MenuItem>
                        )
                      })
                    }
                  </Select>
                </FormControl>
              </Grid>
            ) : undefined
          }
          { isPromoFlowAvailable && (
            <Grid item xs={12}>
              <ToggleButtonGroup
                value={isPromoFlow?.toString()}
                exclusive
                size='small'
                onChange={(e, newValue) => {
                  if (newValue === null) return;

                  setIsPromoFlow(JSON.parse(newValue))
                }}
                aria-label="text alignment"
              >
                <ToggleButton value='false' aria-label="Standard" style={!isPromoFlow ? {backgroundColor: "#142E3E", color: "#28C4FC"} : {backgroundColor: "#00000017", color: "initial"}}>
                  Standard
                </ToggleButton>
                <ToggleButton value='true' aria-label="Promotion" style={isPromoFlow ? {backgroundColor: "#142E3E", color: "#28C4FC"} : {backgroundColor: "#00000017", color: "initial"}}>
                  Promotion
                </ToggleButton>
              </ToggleButtonGroup>
            </Grid>
          )}
        </Grid>
      </Paper>
      {viewMode === 'json' ? (
        <Box style={{ height: '100%', width: '100%', paddingLeft: '380px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
            <Button
              size="small"
              variant="contained"
              style={{ color: '#28C4FC', backgroundColor: '#142E3E' }}
              disabled={graphSource?.type !== 'saved'}
              onClick={handleSaveJson}
            >
              Save JSON
            </Button>
            {graphSource?.readOnly ? (
              <Typography variant="caption" style={{ color: 'rgba(0,0,0,0.6)' }}>
                Standard models are read-only — save a copy to edit the JSON.
              </Typography>
            ) : undefined}
            {jsonError ? (
              <Typography variant="caption" style={{ color: '#b71c1c' }}>
                {jsonError}
              </Typography>
            ) : undefined}
          </div>
          <textarea
            value={jsonText}
            readOnly={graphSource?.type !== 'saved'}
            onChange={(e) => { setJsonText(e.target.value); setJsonError(null); }}
            spellCheck={false}
            style={{
              flex: 1,
              width: '100%',
              fontFamily: 'monospace',
              fontSize: 13,
              padding: 12,
              border: '1px solid rgba(0,0,0,0.23)',
              borderRadius: 5,
              resize: 'none',
              backgroundColor: graphSource?.type !== 'saved' ? '#00000008' : '#ffffff',
            }}
          />
        </Box>
      ) : (
        <Box style={{ height: '100%', width: '100%' }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            fitView
            nodesDraggable={false}
          >
            <Background />
            <Controls />
            <MiniMap nodeColor={nodeColor} zoomable pannable/>
          </ReactFlow>

          {isEditNodeModalOpen ? (
              <EditNodeModal />
            ) : undefined
          }
          {isNewNodeModalOpen ? (
              <NewNodeModal />
            ) : undefined
          }
        </Box>
      )}
    </Paper>
  );
};
